import React, { useState, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { normalize } from '../utils/normalize';

const INITIAL_ITEMS_TO_RENDER = 20;
const MAX_ITEMS_TO_RENDER = 50;
const ITEM_HEIGHT = 60;

const VirtualizedCategories = ({
  data = [],
  selected = [],
  onPress,
  filterText = '',
  isCheckbox = true,
  style = {},
}) => {
  const [expandedStates, setExpandedStates] = useState([]);
  const [visibleItemsCount, setVisibleItemsCount] = useState(INITIAL_ITEMS_TO_RENDER);

  const filteredData = useMemo(() => {
    if (!filterText || filterText.length < 2) {
      return data.slice(0, visibleItemsCount);
    }

    const searchTerm = filterText.toLowerCase();
    const filtered = [];

    for (const state of data) {
      const stateMatches = state.name.toLowerCase().includes(searchTerm);
      
      const matchingCities = state.children?.filter(city =>
        city.name.toLowerCase().includes(searchTerm)
      ) || [];

      if (stateMatches || matchingCities.length > 0) {
        filtered.push({
          ...state,
          children: matchingCities.length > 0 ? matchingCities : state.children,
          isExpanded: matchingCities.length > 0,
        });
      }

      if (filtered.length >= MAX_ITEMS_TO_RENDER) break;
    }

    return filtered;
  }, [data, filterText, visibleItemsCount]);

  const toggleExpand = useCallback((stateSlug) => {
    setExpandedStates(prev => {
      if (prev.includes(stateSlug)) {
        return prev.filter(s => s !== stateSlug);
      }
      return [...prev, stateSlug];
    });
  }, []);

  const handleLoadMore = useCallback(() => {
    if (visibleItemsCount < data.length) {
      setVisibleItemsCount(prev => Math.min(prev + 20, data.length));
    }
  }, [visibleItemsCount, data.length]);

  const renderStateItem = useCallback(({ item: state }) => {
    const isExpanded = expandedStates.includes(state.slug) || state.isExpanded;
    const isSelected = selected.includes(state.slug);
    const hasMatchingCities = filterText && state.children?.some(city =>
      city.name.toLowerCase().includes(filterText.toLowerCase())
    );
    if (state.name?.length === 0) return null;
    return (
      <View key={state.slug} style={{ marginVertical: 4 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <TouchableOpacity
            style={{ flex: 1 }}
            onPress={() => onPress(state.slug, state.children, state.name)}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: isSelected ? 'rgb(233,237,250)' : 'transparent',
                paddingVertical: 12,
                paddingHorizontal: 16,
                borderRadius: 10,
              }}
            >
              <MaterialIcons
                name={isSelected ? 'check-box' : 'check-box-outline-blank'}
                size={20}
                color={isSelected ? 'rgb(72,92,242)' : 'rgba(0,0,0,0.2)'}
              />
              <Text
                style={{
                  marginLeft: 10,
                  fontSize: normalize(14),
                  color: 'rgba(0,0,0,0.8)',
                  fontWeight: hasMatchingCities ? 'bold' : 'normal',
                }}
              >
                {state.name}
              </Text>
            </View>
          </TouchableOpacity>
          
          {state.children?.length > 0 && (
            <TouchableOpacity
              onPress={() => toggleExpand(state.slug)}
              style={{ padding: 10 }}
            >
              <MaterialIcons
                name={isExpanded ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
                size={24}
                color="black"
              />
            </TouchableOpacity>
          )}
        </View>

        {isExpanded && state.children?.length > 0 && (
          <View style={{ marginLeft: 32 }}>
            {state.children.map((city) => {
              const isCitySelected = selected.includes(city.slug);
              return (
                <TouchableOpacity
                  key={city.slug}
                  onPress={() => onPress(city.slug, null, city.name)}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      backgroundColor: isCitySelected ? 'rgb(233,237,250)' : 'transparent',
                      paddingVertical: 10,
                      paddingHorizontal: 16,
                      borderRadius: 10,
                      marginVertical: 2,
                    }}
                  >
                    <MaterialIcons
                      name={isCitySelected ? 'check-box' : 'check-box-outline-blank'}
                      size={20}
                      color={isCitySelected ? 'rgb(72,92,242)' : 'rgba(0,0,0,0.2)'}
                    />
                    <Text
                      style={{
                        marginLeft: 10,
                        fontSize: normalize(14),
                        color: 'rgba(0,0,0,0.8)',
                      }}
                    >
                      {city.name}
                    </Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        )}
      </View>
    );
  }, [expandedStates, selected, onPress, filterText, toggleExpand]);

  const renderFooter = useCallback(() => {
    if (!filterText && visibleItemsCount < data.length) {
      return (
        <TouchableOpacity
          onPress={handleLoadMore}
          style={{
            paddingVertical: 15,
            alignItems: 'center',
            marginTop: 10,
          }}
        >
          <Text style={{ color: '#007AFF', fontSize: 16 }}>
            Load More ({data.length - visibleItemsCount} remaining)
          </Text>
        </TouchableOpacity>
      );
    }
    return null;
  }, [filterText, visibleItemsCount, data.length, handleLoadMore]);

  const keyExtractor = useCallback((item) => item.slug, []);

  const getItemLayout = useCallback((data, index) => ({
    length: ITEM_HEIGHT,
    offset: ITEM_HEIGHT * index,
    index,
  }), []);

  if (data.length === 0) {
    return (
      <View style={{ padding: 20 }}>
        <Text style={{ textAlign: 'center', color: '#666' }}>
          No locations available
        </Text>
      </View>
    );
  }

  if (filterText.length > 0 && filterText.length < 2) {
    return (
      <View style={{ padding: 20 }}>
        <Text style={{ textAlign: 'center', color: '#666' }}>
          Type at least 2 characters to search
        </Text>
      </View>
    );
  }

  return (
    <FlatList
      data={filteredData}
      renderItem={renderStateItem}
      keyExtractor={keyExtractor}
      getItemLayout={getItemLayout}
      initialNumToRender={INITIAL_ITEMS_TO_RENDER}
      maxToRenderPerBatch={10}
      windowSize={10}
      removeClippedSubviews={true}
      ListFooterComponent={renderFooter}
      style={style}
      showsVerticalScrollIndicator={false}
      onEndReachedThreshold={0.5}
      onEndReached={handleLoadMore}
    />
  );
};

export default VirtualizedCategories;